import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

// Only handles the welcome offer after its activation link is used.
const config = JSON.parse(document.getElementById('knise-welcome-gift-config').textContent);
const root = window.Shopify.routes.root;
const code = 'VELKOMSTKRUS';
const variant = 44255066488920;
const marker = '_knise_velkomstgave';
let running;
let nextRun;
let rerun = false;
let active = config.eligible;
const activation = new URL(location.href).searchParams.get('knise_gave') === '1';
let entitlement;
try {
  entitlement = JSON.parse(localStorage.getItem('knise-welcome-gift'));
  if (!entitlement || typeof entitlement !== 'object' || !entitlement.expires || entitlement.expires < Date.now()) entitlement = undefined;
  active ||= !!entitlement;
} catch (_) {}
if (config.knownCustomer && !config.eligible) active = false;
if (activation && !config.ineligible) {
  active = true;
  entitlement = {expires: Date.now() + 30 * 86400000};
}

function rememberCart(cart) {
  // Shopify can replace a cart token during ordinary shopping. Eligibility and
  // one-use limits are enforced by the native discount at checkout.
  if (active && !config.ineligible) {
    entitlement ||= {expires: Date.now() + 30 * 86400000};
    delete entitlement.token;
    delete entitlement.blocked;
  }
  try {
    if (entitlement) localStorage.setItem('knise-welcome-gift', JSON.stringify(entitlement));
    else localStorage.removeItem('knise-welcome-gift');
  } catch (_) {}
}

async function request(endpoint, body) {
  const response = await fetch(root + endpoint, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: body ? {'Content-Type': 'application/json'} : {},
    body: body ? JSON.stringify(body) : undefined,
    signal: AbortSignal.timeout(10000)
  });
  if (!response.ok) throw new Error('Gaven kunne ikke oppdateres.');
  return response.json();
}

function giftLines(cart) {
  return cart.items.filter(item => item.variant_id === variant && item.properties?.[marker] === '1');
}

function paidValue(cart) {
  return cart.items.reduce((sum, item) => sum + (item.properties?.[marker] === '1' ? 0 : item.original_line_price), 0);
}

function sectionIds() {
  return [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map(el => el.dataset.sectionId))].slice(0, 5);
}

async function refreshCart(cart) {
  const drawerWasOpen = document.querySelector('cart-drawer-component dialog')?.open;
  const ids = sectionIds();
  if (ids.length) {
    const sections = await fetch(root + '?sections=' + ids.map(encodeURIComponent).join(','), {signal: AbortSignal.timeout(5000)}).then(response => response.json());
    ids.forEach(id => {if (sections[id]) morphSection(id, sections[id]);});
  }
  if (drawerWasOpen) document.querySelector('cart-drawer-component')?.open();
  document.dispatchEvent(new CartUpdateEvent(cart, 'knise-welcome-gift', {source: 'knise-welcome-gift', itemCount: cart.item_count}));
}

async function reconcile() {
  if (running) {rerun = true; return running;}
  running = (async () => {
    do {
    rerun = false;
    let cart = await request('cart.js');
    rememberCart(cart);
    const gifts = giftLines(cart);
    const qualifies = active && !config.ineligible && cart.currency === 'NOK' && paidValue(cart) >= 100000;
    let changed = false;
    if (!qualifies && gifts.length) {
      for (const gift of gifts) {cart = await request('cart/change.js', {id: gift.key, quantity: 0});}
      changed = true;
    } else if (qualifies) {
      // Different properties keep the customer's paid white mugs separate from the gift.
      if (!gifts.length) {
        await request('cart/add.js', {items: [{id: variant, quantity: 1, properties: {[marker]: '1', 'Velkomstgave': 'Knise-epostlisten 🤍'}}]});
        changed = true;
      } else if (gifts.length > 1 || gifts[0].quantity !== 1) {
        for (let i = 0; i < gifts.length; i++) {await request('cart/change.js', {id: gifts[i].key, quantity: i === 0 ? 1 : 0});}
        changed = true;
      }
      const codes = (cart.discount_codes || []).map(item => item.code);
      if (changed || !codes.some(item => item.toUpperCase() === code)) {
        await request('cart/update.js', {discount: [...codes, code].join(',')});
        changed = true;
      }
    }
    if (changed) {cart = await request('cart.js'); await refreshCart(cart);}
    if (!rerun) return cart;
    } while (rerun);
  })().finally(() => {running = undefined;});
  return running;
}

function schedule() {
  if (!active && !config.ineligible) return;
  clearTimeout(nextRun);
  nextRun = setTimeout(() => reconcile().catch(() => {}), 250);
}
document.addEventListener('cart:update', event => {
  if (event.detail?.sourceId !== 'knise-welcome-gift') schedule();
});
document.addEventListener('discount:update', schedule);
window.addEventListener('pageshow', schedule);
// Older welcome emails use Shopify's native discount link without the activation parameter.
request('cart.js').then(cart => {
  if (!active && !config.ineligible && (!config.knownCustomer || config.eligible) &&
      (cart.discount_codes || []).some(item => item.code.toUpperCase() === code)) active = true;
  rememberCart(cart);
  if (active || giftLines(cart).length) reconcile().catch(() => {});
}).catch(() => {});

// Wait for the latest cart to be reconciled before the regular checkout submit.
document.addEventListener('submit', event => {
  const discountInput = event.target?.querySelector?.('input[name="discount"]');
  if (discountInput?.value.trim().toUpperCase() === code) {
    event.preventDefault();
    event.stopImmediatePropagation();
    active = true;
    entitlement = {expires: Date.now() + 30 * 86400000};
    const button = event.submitter;
    if (button) button.disabled = true;
    reconcile().then(cart => {
      if (paidValue(cart) < 100000) alert('Handle for minst 1 000 kr i betalte varer for å få gavekruset. Gave og frakt teller ikke med.');
      else if (!(cart.discount_codes || []).some(item => item.code.toUpperCase() === code && item.applicable)) {
        alert('Gavekoden kunne ikke brukes. Den gjelder én gang per kunde og kan ikke kombineres med andre prisrabatter.');
      }
    }).catch(() => {
      alert('Vi kunne ikke oppdatere gavekruset. Prøv igjen.');
    }).finally(() => {if (button) button.disabled = false;});
    return;
  }
  if (!active || event.submitter?.name !== 'checkout') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const submitter = event.submitter;
  const wasDisabled = submitter.disabled;
  submitter.disabled = true;
  reconcile().then(() => {
    location.assign(root + 'checkout');
  }).catch(() => {
    alert('Vi kunne ikke oppdatere gavekruset. Prøv å gå til kassen igjen.');
  }).finally(() => {submitter.disabled = wasDisabled;});
}, true);

// Compact the existing Forms popup in its own shadow root. No signup data is read.
const popupCSS = `
@media (max-width: 600px) {
  [data-sizing="form-wrapper"] {max-height: calc(100dvh - 24px) !important; overflow-y:auto !important;}
  section[data-sizing="form-wrapper"][class*="grid"] {grid-template-rows:158px auto !important;}
  [data-sizing="form-wrapper"] [class*="gridItem"][style*="background-image"] {
    height:158px !important; min-height:158px !important; max-height:158px !important;
    background-size:auto 226px !important; background-position:center !important;
  }
  [class*="gridItemContent"] {padding:16px 20px !important; gap:10px !important;}
  [data-sizing="form-wrapper"] [class*="formHeader"] {margin:0 0 10px !important; gap:7px !important;}
  h2 {font-size:23px !important; line-height:1.12 !important; margin:0 0 7px !important;}
  p {line-height:1.35 !important; margin-top:0 !important; margin-bottom:0 !important;}
  form {gap:8px !important;}
  [data-sizing="form-wrapper"] [class*="formDisclaimer"] {margin-top:10px !important; padding:0 !important; font-size:11px !important; line-height:1.3 !important;}
}
`;
function compactForms(scope = document) {
  for (const el of scope.querySelectorAll('*')) {
    if (!el.shadowRoot) continue;
    const shadow = el.shadowRoot;
    if ((shadow.querySelector('[data-sizing="form-wrapper"]') || el.localName.includes('forms')) && !shadow.getElementById('knise-mobile-form-style')) {
      const style = document.createElement('style');
      style.id = 'knise-mobile-form-style';
      style.textContent = popupCSS;
      shadow.appendChild(style);
    }
    compactForms(shadow);
  }
}
let scanCount = 0;
const formScan = setInterval(() => {
  compactForms();
  if (++scanCount > 45) clearInterval(formScan);
}, 1000);

